import { InvoiceSettlementComponent } from './modules/app-components/invoice-settlement/invoice-settlement.component';
import { PrintLogDialogComponent } from './modules/app-components/print-log-dialog/print-log-dialog.component';
import { NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { CurrencyPipe, DatePipe, DecimalPipe, HashLocationStrategy, LocationStrategy } from '@angular/common';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { PinchZoomModule } from '@meddv/ngx-pinch-zoom';

import { CookieModule, CookieService } from 'ngx-cookie';
import { ToastrModule } from 'ngx-toastr';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { QuicklinkModule } from 'ngx-quicklink';
import { ImageCropperModule } from 'ngx-image-cropper';

import { LoadingBarHttpClientModule } from '@ngx-loading-bar/http-client';
import { IntlModule } from '@progress/kendo-angular-intl';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';

import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { SidebarModule } from 'primeng/sidebar';
import { TabViewModule } from 'primeng/tabview';
import { TableModule } from 'primeng/table';
import { CheckboxModule } from 'primeng/checkbox';
import { AccordionModule } from 'primeng/accordion';
import { CalendarModule } from 'primeng/calendar';
import { InputSwitchModule } from 'primeng/inputswitch';
import { TooltipModule } from 'primeng/tooltip';
import { CarouselModule } from 'primeng/carousel';

import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { AngularImageViewerModule } from 'angular-x-image-viewer';

import { FormatService } from 'app/services/format.service';
import { DateService } from 'app/services/date.service';
import { FileService } from 'app/services/file.service';
import { AttachmentService } from 'app/services/attachment.service';
import { SocketService } from 'app/services/socket.service';
import { CalculationService } from 'app/services/calculation.service';
import { GeoLocationService } from 'app/services/geo-location.service';
import { ExportService } from 'app/services/export.service';
import { AccessGuard } from 'app/services/access.guard.service';
import { AuthGuard } from 'app/services/auth.guard.service';
import { HelperService } from 'app/services/helper.service';
import { DataService } from 'app/services/data.service';
import { ValidationService } from 'app/services/validation.service';
import { ACLService } from 'app/services/acl.service';
import { HTTPService } from 'app/services/http.service';
import { SharedService } from 'app/services/shared.service';
import { LocationAddComponent } from 'app/modules/app-components/location-add/location-add.component';
import { EmailEditorModule } from 'angular-email-editor';

import { AppRoutingModule } from 'app/app.routing';
import { AppComponent } from 'app/app.component';
import { MasterLayoutComponent, TechLayoutComponent } from 'app/layouts';
import { SharedModule } from 'app/modules/shared/shared.module';
import { AppClockModule } from './modules/app-components/clock/clock.module';
import { TechSharedModule } from './modules/technician/tech-shared.module';

import { MktSMSTemplate } from 'app/modules/app-components/mkt-sms-template-dialog/mkt-sms-template-dialog.component';
import {
  AddDialogComponent,
  BlockAppointmentDialogComponent,
  DeclineAppointmentRequestComponent,
  AddSuccessfullyDialogComponent,
  ConnectToMotorDialogComponent,
  AddTechnicianAttendenceDialogueComponent,
  AppointmentComponent,
  ApptVerifyComponent,
  AssignTechnicianDialogComponent,
  CancelApptDialogComponent,
  CannedInspectionAddComponent,
  CannedInspectionBrowseComponent,
  CannedServiceBrowseComponent,
  CannedServiceDialogComponent,
  CarfaxVehicleHistoryComponent,
  CreateTireStorageModal,
  CustomerAddComponent,
  CustomerEstimateComponent,
  CustomerHistoryImportedDialogComponent,
  CustomerInspectionsComponent,
  CustomerUpdateComponent,
  DeferredServicesComponent,
  DeferredServiceSidelistComponent,
  DialogFollowUpCardComponent,
  DialogFollowUpsComponent,
  EditTechnicianAttendenceDialogueComponent,
  FeeAddComponent,
  FollowUpSidepanelComponent,
  ForgotPasswordComponent,
  GlobalSearchComponent,
  GlobalSearchItemComponent,
  HeaderComponent,
  ImageCropperDialogComponent,
  InspectionCategoryComponent,
  InspectionCategoryItemComponent,
  DVIInspectionCategoryComponent,
  DVIInspectionCategoryItemComponent,
  InspectionItemNotesComponent,
  InternalServicesComponent,
  InvoiceCounterComponent,
  ItemDialogComponent,
  LaborAddComponent,
  LoginComponent,
  MaintainanceServicesComponent,
  MotorServicesComponent,
  MovedInvoicesStatementDialogComponent,
  MultiTechAssignDialogComponent,
  MultiTechSelectionRowComponent,
  P404Component,
  PartAddComponent,
  NewPartAddComponent,
  PaymentAddComponent,
  PurchasePaymentOrder,
  PersonalInfoCardComponent,
  RdBulletinsComponent,
  RdComponentDiagramComponent,
  RdProceduresComponent,
  RdSpecsComponent,
  RdWiringsComponent,
  ReleaseNotesComponent,
  ReminderItemComponent,
  RepairDiagramDialogComponent,
  ResetPasswordComponent,
  ReviewDialogComponent,
  SendDialogComponent,
  SendInspectionComponent,
  ServiceAuthorisationDialogComponent,
  AuthorizationPartsOrderingComponent,
  ServiceItemComponent,
  SidebarComponent,
  SidebarUpcomingFollowUpComponent,
  SidepanelFollowUpCampaignComponent,
  SupplierAddComponent,
  TechFlagDialogComponent,
  TireAddComponent,
  TireSizeComponent,
  TireStorageInfoComponent,
  TrialCampaignDialogComponent,
  TroubleshootCodeComponent,
  UnsubscribeContactComponent,
  UnsyncVehicleDialogComponent,
  UpcomingAppointmentComponent,
  UpdateApptDialogComponent,
  UpdateContactDialogComponent,
  UpdateRecipientDialogComponent,
  VechicleItemComponent,
  VehicleAddComponent,
  VehicleSideListComponent,
  DiscountAddComponent,
  DuplicateVehicleDialogComponent,
  MergeVehicleDialogComponent,
  MergeCustomerDialogComponent,
  SupplierSidebarComponent,
  SupplierUpdateComponent,
  UpcomingSupplierPosComponent,
  PoCardComponent,
  RoCardComponent,
  CreditPaymentAddComponent,
  CustomerCreditMemoComponent,
  ApplyCreditPaymentComponent,
  AuthorizationCorePartsComponent,
  ReassignDialogComponent,
  ReportScheduleComponent,
  MotorRepairGuidesComponent,
  NavigateCompanyViewComponent,
  AddLeadSourceComponent,
  TransferVehicleDialogComponent,
  NotificationSidepanelComponent,
  InvoiceAdjustmentDialogComponent,
  TireAddDotNumberComponent,
  AuthorizationTireDotNumberComponent,
  ArAccountComponent,
  CreateUpdateCategoryComponent,
  SalesCodeAddDialogComponent,
  WarrantyAddDialogComponent,
  WarrantyPolicyTermsComponent,
  SalesCodeAssignDialogComponent,
  CreateMyFleetCustomerDialogComponent,
  MyFleetCustomerDialogAddressComponent,
  DriverSideListComponent,
  DriverSideItemListComponent,
  LocationsDropdownDialogComponent,
  AddCustomOverridesDialogComponent,
  TireStorageSidepanelComponent,
  InspectionDialogComponent,
} from 'app/modules/app-components';
import { ServiceLookupComponent } from 'app/modules/app-components/service-lookup/service-lookup.component';

import { UpdateCannedServiceDialogComponent } from 'app/modules/app-components/update-canned-service-dialog/update-canned-service-dialog.component';
import { DeleteCategoryDialogComponent } from 'app/modules/app-components/delete-category-dialog/delete-category-dialog.component';
import { PromPreferredLocationDialogComponent } from 'app/modules/app-components/prom-preferred-location-dialog/prom-preferred-location-dialog.component';
import { InspectionItemAddComponent } from 'app/modules/app-components/inspection-item-add/inspection-item-add.component';
import { UpdateInspectionsModalComponent } from 'app/modules/app-components/update-inspections-modal/update-inspections-modal.component';
import { PdfImageUploaderComponent } from 'app/modules/app-components/pdf-image-uploader/pdf-image-uploader.component';
import { ChangePasswordComponent } from 'app/modules/app-components/change-password/change-password.component';
import { ReviewSettingDialogComponent } from 'app/modules/reviews/components/review-setting-dialog/review-setting-dialog.component';
import { OnboardingChecklistComponent } from 'app/modules/app-components/onboarding-checklist/onboarding-checklist.component';
import { SubscriptionExpiryModalComponent } from 'app/modules/app-components/subscription-expiry-modal/subscription-expiry-modal.component';

import { RefundPaymentDialogComponent } from 'app/modules/app-components/refund-payment-dialog/refund-payment-dialog.component';
import { SubscriptionWelcomeModalComponent } from 'app/modules/app-components/subscription-welcome-modal/subscription-welcome-modal.component';
import { UpdateStockComponent } from 'app/modules/inventory/components/update-stock/update-stock.component';
import { MktEmailTemplate } from './modules/app-components/mkt-email-template-dialog/mkt-email-template-dialog.component';
import { AddGroupComponent } from './modules/app-components/add-group/add-group.component';
import { AddCampaignComponent } from './modules/app-components/add-campaign/add-campaign.component';
import { SendStatementDialogComponent } from './modules/app-components/send-statement-dialog/send-statement-dialog.component';
import { CreateBulkPaymentDialogComponent } from './modules/app-components/create-bulk-payment-dialog/create-bulk-payment-dialog.component';

import { RenameAttachmentDialogComponent } from './modules/app-components/rename-attachment-dialog/rename-attachment-dialog.component';
import { MktTemplateType } from './modules/app-components/mkt-template-type-dialog/mkt-template-type-dialog.component';
import { PauseFollowUpsDialogComponent } from './modules/follow-up-campaign/components';
import { AddVehicleHistoryComponent } from './modules/app-components/add-vehicle-history/add-vehicle-history.component';
import { HighlightSearch, VehicleHistoryServicesComponent } from './modules/app-components/vehicle-history-services/vehicle-history-services.component';
import { AnnouncementTemplateView } from './modules/marketing/components/announcement-email-template/announcement-template-view.component';
import { ProductTemplateViewComponent } from './modules/marketing/components/product-template-view/product-template-view.component';
import { PurchaseOrderListDialogComponent } from 'app/modules/inventory/components/purchase-order-list-dialog/purchase-order-list-dialog.component';
import { SendPreviewTemplate } from 'app/modules/marketing/components/send-template-view/send-template-view.component';
import { FleetCustomerComponent } from './modules/app-components/fleet-customer/fleet-customer/fleet-customer.component';
import { CustomFleetPricingComponent } from './modules/app-components/custom-fleet-pricing/custom-fleet-pricing/custom-fleet-pricing.component';
import { CustomFleetSetting } from './modules/app-components/fleet-settings/custom-fleet-setting/custom-fleet-setting.component';
import { MyFleetOverrideSetting } from './modules/app-components/fleet-settings/my-fleet-override-setting/my-fleet-override-setting.component';
import { AddTireStorageModal } from './modules/app-components/add-tire-storage/add-tire-storage.component';
import { AppCreateStatementDialogModule } from './modules/app-components/create-statement-dialog/create-statement-dialog.module';
import { AppSupplierBulkPaymentModule } from './modules/app-components/supplier-bulk-payment/supplier-bulk-payment.module';
import { PayrixPaymentFieldComponent } from './modules/app-components/payment-add/payrix-payment-field/payrix-payment-field.component';
import { MotorServiceEnhancedComponent } from './modules/app-components/canned-service-browse/components/motor-service-enhanced/motor-service-enhanced.component';
import { ViewPaymentComponent } from './modules/app-components/view-payment/view-payment.component';
import { ProcedureDiagramDialogComponent } from './modules/app-components/procedure-diagram-dialog/procedure-diagram-dialog.component';
import { SetPrefrencesComponent } from './modules/app-components/create-prefrences/set-prefrences/set-prefrences.component';
import { SavePrefrencesComponent } from './modules/app-components/create-prefrences-save/save-prefrences/save-prefrences.component';
import { SwitchLocationDialogComponent } from './modules/app-components/switch-location-dialog/switch-location-dialog.component';
import { SwitchCorporateDialogComponent } from './modules/app-components/switch-corporate-dialog/switch-corporate-dialog.component';
import { SwipeCardConfirmationComponent } from './modules/app-components/payment-add/swipe-card-confirmation/swipe-card-confirmation.component';
import { OilChangeDialogComponent } from './modules/app-components/oil-change-dialog/oil-change-dialog.component';
import { EndDayReconciliationDialogComponent } from './modules/app-components/end-day-reconciliation-dialog/end-day-reconciliation-dialog.component';
import { MarketingEmailTemplateViewComponent } from './modules/marketing/components/marketing-email-template/marketing-email-template-view.component';
import { Mitchell1ServiceAssignDialogComponent } from './modules/estimate/components/mitchell1-assign-dialog/mitchell1-service-assign-dialog.component';
import { ManualOrderedServicePartsComponent } from './modules/app-components/manual-ordered-service-parts/manual-ordered-service-parts.component';
import { FlatRateAddComponent } from './modules/app-components/flat-rate-add/flat-rate-add.component';
import { FlatRateSubItemComponent } from './modules/app-components/flat-rate-add/flat-rate-sub-item/flat-rate-sub-item.component';
import { ManualPartsOrderComponent } from './modules/estimate/components/manual-parts-order/manual-parts-order.component';
import { AppTabLinkModule } from './modules/shared/components/open-tab-link/open-tab-link.module';
import { PagerService } from './services/pager.service';
import { ScheduledFollowupDetailsComponent } from './modules/marketing/components/scheduled-followup-details/scheduled-followup-details.component';
import { CalculatorChangeDialogComponent } from './modules/shared/components/calculator-change-dialog/calculator-change-dialog.component';
import { ServiceAuthorizationSkeletonComponent } from './modules/app-components/service-authorisation-dialog/service-authorization-skeleton/service-authorization-skeleton.component';
import { GlobalPaymentFieldComponent } from './modules/app-components/payment-add/global-payment-field/global-payment-field.component';
import { CdkScrollableModule } from '@angular/cdk/scrolling';
import { AuthPendingOrderedCartsComponent } from './modules/app-components/service-authorisation-dialog/auth-pending-ordered-carts/auth-pending-ordered-carts.component';
import { TireDotNumberDialogComponent } from './modules/app-components/tire-dot-number-dialog/tire-dot-number-dialog.component';
import { SelectedItemsService } from './services/selectedItems.service';
import { MotorServiceEnhancedNewGenComponent } from './modules/app-components/canned-service-browse/components/motor-service-enhanced-new-gen/motor-service-enhanced-new-gen.component';
import { CatalogActivityLogComponent } from './modules/app-components/catalog-activity-logs/catalog-activity-logs.component';
import { AuthorizationItemClassificationComponent } from './modules/app-components/service-authorisation-dialog/authorization-item-classification/authorization-item-classification.component';
import { DeleteCategoryEnhancedDialogComponent } from './modules/app-components/delete-category-enhanced-dialog/delete-category-enhanced-dialog.component';
import { AddonServiceDialogComponent } from './modules/estimate/components/addon-service-dialog/addon-service-dialog.component';
import { TireAddNewComponent } from './modules/app-components/new-tire-add/new-tire-add.component';
import { ViewInspectionChecklistsDialogComponent } from './modules/app-components/view-inspection-checklists-dialog/view-inspection-checklists-dialog.component';
import { AuthorizationSalesCodeAssignmentComponent } from './modules/app-components/service-authorisation-dialog/authorization-sales-code-assignment/authorization-sales-code-assignment.component';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { CustomTranslateLoader } from './services/custom-translate-loader';
import { HealthFrontEndComponent } from './modules/app-components/health-frontend/health-frontend.component';
import { TireStorageDetailsComponent } from './modules/app-components/tire-storage-details/tire-storage-details.component';
import { NewTireStorageModalComponent } from './modules/app-components/new-tire-storage-modal/new-tire-storage-modal.component';
import { TireInOutModalComponent } from './modules/app-components/tire-in-out-modal/tire-in-out-modal.component';
import { QtyOnEstimateListModalComponent } from './modules/app-components/qty-on-estimate-list-modal/qty-on-estimate-list-modal.component';
import { PartPricingModule } from './modules/settings/modules/pricing/parts-pricing/parts-pricing.module';
import { RedirectComponent } from './redirect/redirect.component';
import { WarrantyService } from './services/warranty-service';
import { WarrantyAssignDialogComponent } from './modules/app-components/warranty-assign-dialog/warranty-assign-dialog.component';

@NgModule({
  imports: [
    PinchZoomModule,
    BrowserModule,
    RouterModule,
    SharedModule,
    PartPricingModule,
    InputSwitchModule,
    HttpClientModule,
    BrowserAnimationsModule,
    LoadingBarHttpClientModule,
    QuicklinkModule,
    DynamicDialogModule,
    CheckboxModule,
    SidebarModule,
    TabViewModule,
    AccordionModule,
    CalendarModule,
    TableModule,
    TooltipModule,
    CarouselModule,
    DragDropModule,
    ImageCropperModule,
    IntlModule,
    DateInputsModule,
    NgxSkeletonLoaderModule.forRoot(),
    NgxMaskDirective,
    NgxMaskPipe,
    CookieModule.forRoot(),
    ToastrModule.forRoot({
      preventDuplicates: true,
    }),
    AppRoutingModule,
    AngularImageViewerModule,
    EmailEditorModule,
    AppCreateStatementDialogModule,
    AppSupplierBulkPaymentModule,
    TechSharedModule,
    AppClockModule,
    AppTabLinkModule,
    CdkScrollableModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: CustomTranslateLoader,
        deps: [HttpClient, CookieService, DataService, HTTPService],
      },
    }),
  ],
  declarations: [
    ArAccountComponent,
    Mitchell1ServiceAssignDialogComponent,
    AppComponent,
    MasterLayoutComponent,
    P404Component,
    LoginComponent,
    ForgotPasswordComponent,
    HealthFrontEndComponent,
    HeaderComponent,
    GlobalSearchComponent,
    SidebarComponent,
    CustomerAddComponent,
    VehicleAddComponent,
    AddSuccessfullyDialogComponent,
    ConnectToMotorDialogComponent,
    CustomerUpdateComponent,
    VehicleSideListComponent,
    DeferredServiceSidelistComponent,
    VechicleItemComponent,
    ApptVerifyComponent,
    AddDialogComponent,
    BlockAppointmentDialogComponent,
    DeclineAppointmentRequestComponent,
    AppointmentComponent,
    ReminderItemComponent,
    UpcomingAppointmentComponent,
    CancelApptDialogComponent,
    PartAddComponent,
    NewPartAddComponent,
    DiscountAddComponent,
    CannedInspectionAddComponent,
    CannedServiceDialogComponent,
    CreateUpdateCategoryComponent,
    SalesCodeAddDialogComponent,
    WarrantyAddDialogComponent,
    ServiceItemComponent,
    CannedServiceBrowseComponent,
    CannedInspectionBrowseComponent,
    ResetPasswordComponent,
    TechLayoutComponent,
    UpdateApptDialogComponent,
    PaymentAddComponent,
    InvoiceAdjustmentDialogComponent,
    PurchasePaymentOrder,
    ReviewDialogComponent,
    ReviewSettingDialogComponent,
    CustomerEstimateComponent,
    CustomerInspectionsComponent,
    SendDialogComponent,
    GlobalSearchItemComponent,
    PersonalInfoCardComponent,
    ImageCropperDialogComponent,
    LaborAddComponent,
    FeeAddComponent,
    TireAddComponent,
    TireSizeComponent,
    TireAddNewComponent,
    NewTireStorageModalComponent,
    TireInOutModalComponent,
    ReleaseNotesComponent,
    InspectionCategoryComponent,
    InspectionCategoryItemComponent,
    DVIInspectionCategoryComponent,
    DVIInspectionCategoryItemComponent,
    AssignTechnicianDialogComponent,
    InspectionItemNotesComponent,
    UpdateCannedServiceDialogComponent,
    DeleteCategoryDialogComponent,
    DeleteCategoryEnhancedDialogComponent,
    PromPreferredLocationDialogComponent,
    InspectionItemAddComponent,
    UpdateInspectionsModalComponent,
    PdfImageUploaderComponent,
    CarfaxVehicleHistoryComponent,
    InspectionDialogComponent,
    UnsyncVehicleDialogComponent,
    ChangePasswordComponent,
    LocationAddComponent,
    ItemDialogComponent,
    InvoiceCounterComponent,
    InternalServicesComponent,
    MotorServicesComponent,
    MotorServiceEnhancedComponent,
    MotorServiceEnhancedNewGenComponent,
    MaintainanceServicesComponent,
    DeferredServicesComponent,
    TroubleshootCodeComponent,
    ServiceAuthorisationDialogComponent,
    AuthorizationPartsOrderingComponent,
    AuthorizationTireDotNumberComponent,
    AuthorizationCorePartsComponent,
    UpdateContactDialogComponent,
    UpdateRecipientDialogComponent,
    RepairDiagramDialogComponent,
    ProcedureDiagramDialogComponent,
    OnboardingChecklistComponent,
    RdComponentDiagramComponent,
    RdSpecsComponent,
    RdProceduresComponent,
    RdWiringsComponent,
    RdBulletinsComponent,
    SubscriptionExpiryModalComponent,
    RefundPaymentDialogComponent,
    SubscriptionWelcomeModalComponent,
    UpdateStockComponent,
    QtyOnEstimateListModalComponent,
    MktEmailTemplate,
    MktSMSTemplate,
    AnnouncementTemplateView,
    MarketingEmailTemplateViewComponent,
    ProductTemplateViewComponent,
    SendPreviewTemplate,
    AddGroupComponent,
    CustomerHistoryImportedDialogComponent,
    AddCampaignComponent,
    TechFlagDialogComponent,
    SendStatementDialogComponent,
    CreateBulkPaymentDialogComponent,
    MovedInvoicesStatementDialogComponent,
    RenameAttachmentDialogComponent,
    MktTemplateType,
    UnsubscribeContactComponent,
    FollowUpSidepanelComponent,
    SidepanelFollowUpCampaignComponent,
    PauseFollowUpsDialogComponent,
    SidebarUpcomingFollowUpComponent,
    DialogFollowUpsComponent,
    DialogFollowUpCardComponent,
    AddVehicleHistoryComponent,
    AddTireStorageModal,
    MultiTechAssignDialogComponent,
    MultiTechSelectionRowComponent,
    VehicleHistoryServicesComponent,
    HighlightSearch,
    TrialCampaignDialogComponent,
    ServiceLookupComponent,
    PurchaseOrderListDialogComponent,
    SendInspectionComponent,
    CreateTireStorageModal,
    TireStorageInfoComponent,
    FleetCustomerComponent,
    CustomFleetPricingComponent,
    CustomFleetSetting,
    MyFleetOverrideSetting,
    SupplierAddComponent,
    PayrixPaymentFieldComponent,
    GlobalPaymentFieldComponent,
    AddTechnicianAttendenceDialogueComponent,
    EditTechnicianAttendenceDialogueComponent,
    ViewPaymentComponent,
    ProcedureDiagramDialogComponent,
    SetPrefrencesComponent,
    SavePrefrencesComponent,
    SwitchLocationDialogComponent,
    SwitchCorporateDialogComponent,
    SwipeCardConfirmationComponent,
    OilChangeDialogComponent,
    DuplicateVehicleDialogComponent,
    ReassignDialogComponent,
    EndDayReconciliationDialogComponent,
    ManualOrderedServicePartsComponent,
    MergeVehicleDialogComponent,
    MergeCustomerDialogComponent,
    FlatRateAddComponent,
    FlatRateSubItemComponent,
    ManualPartsOrderComponent,
    PrintLogDialogComponent,
    AuthPendingOrderedCartsComponent,
    ScheduledFollowupDetailsComponent,
    SupplierSidebarComponent,
    SupplierUpdateComponent,
    UpcomingSupplierPosComponent,
    PoCardComponent,
    RoCardComponent,
    CreditPaymentAddComponent,
    CustomerCreditMemoComponent,
    ApplyCreditPaymentComponent,
    CalculatorChangeDialogComponent,
    ReportScheduleComponent,
    ServiceAuthorizationSkeletonComponent,
    MotorRepairGuidesComponent,
    NavigateCompanyViewComponent,
    AddLeadSourceComponent,
    TransferVehicleDialogComponent,
    NotificationSidepanelComponent,
    InvoiceSettlementComponent,
    TireDotNumberDialogComponent,
    TireAddDotNumberComponent,
    CatalogActivityLogComponent,
    AuthorizationItemClassificationComponent,
    AddonServiceDialogComponent,
    ViewInspectionChecklistsDialogComponent,
    WarrantyPolicyTermsComponent,
    SalesCodeAssignDialogComponent,
    WarrantyAssignDialogComponent,
    CreateMyFleetCustomerDialogComponent,
    MyFleetCustomerDialogAddressComponent,
    AuthorizationSalesCodeAssignmentComponent,
    DriverSideListComponent,
    DriverSideItemListComponent,
    TireStorageDetailsComponent,
    LocationsDropdownDialogComponent,
    AddCustomOverridesDialogComponent,
    RedirectComponent,
    TireStorageSidepanelComponent,
  ],
  providers: [
    AuthGuard,
    AccessGuard,
    HTTPService,
    SharedService,
    HelperService,
    ACLService,
    DataService,
    ValidationService,
    ExportService,
    CalculationService,
    SocketService,
    DateService,
    FileService,
    SelectedItemsService,
    AttachmentService,
    DialogService,
    FormatService,
    PagerService,
    CurrencyPipe,
    DatePipe,
    DecimalPipe,
    {
      provide: LocationStrategy,
      useClass: HashLocationStrategy,
    },
    GeoLocationService,
    WarrantyService,
    provideNgxMask(),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
